import { z } from 'zod';
import { errResult } from 'libs/end-to-end/src/result';
import { okResult } from 'libs/end-to-end/src/result';
import { Result } from 'libs/end-to-end/src/result';
import { ZodParseError } from 'libs/end-to-end/src/types/errors/zod-parse-error';
import { createZodParseError } from 'libs/end-to-end/src/utils/utility/create-zod-parse-error';

export const SimpleObject = z.record(z.string(), z.unknown());
export type SimpleObject = z.infer<typeof SimpleObject>;

export const SimpleStringArray = z.array(z.string());
export type SimpleStringArray = z.infer<typeof SimpleStringArray>;

export const SimpleObjectsArray = z.array(SimpleObject);
export type SimpleObjectsArray = z.infer<typeof SimpleObjectsArray>;

export const asSimpleObject = (
  input: unknown,
): Result<SimpleObject, ZodParseError> => {
  const parseResult = SimpleObject.safeParse(input);
  if (!parseResult.success) {
    return errResult(createZodParseError(parseResult));
  }

  return okResult(parseResult.data);
};

export const asSimpleStringArray = (
  input: unknown,
): Result<SimpleStringArray, ZodParseError> => {
  const parseResult = SimpleStringArray.safeParse(input);
  if (!parseResult.success) {
    return errResult(createZodParseError(parseResult));
  }

  return okResult(parseResult.data);
};

export const asSimpleObjectsArray = (
  input: unknown,
): Result<SimpleObjectsArray, ZodParseError> => {
  const parseResult = SimpleObjectsArray.safeParse(input);
  if (!parseResult.success) {
    return errResult(createZodParseError(parseResult));
  }

  return okResult(parseResult.data);
};

export const copyObjectWithKeysSorted = <TSimpleObject extends SimpleObject>(
  obj: TSimpleObject,
): TSimpleObject => {
  const sortedKeys = Object.keys(obj).sort();
  const newObj: SimpleObject = {};
  for (const key of sortedKeys) {
    const item = obj[key];
    const itemAsSimpleObjectResult = asSimpleObject(item);

    if (itemAsSimpleObjectResult.resultType === 'ok') {
      newObj[key] = copyObjectWithKeysSorted(itemAsSimpleObjectResult.value);
    } else {
      const itemAsSimpleStringArrayResult = asSimpleStringArray(item);
      if (itemAsSimpleStringArrayResult.resultType === 'ok') {
        newObj[key] = itemAsSimpleStringArrayResult.value.sort((a, b) => {
          return a.localeCompare(b);
        });
      } else {
        const itemAsSimpleObjectsArray = asSimpleObjectsArray(item);

        if (itemAsSimpleObjectsArray.resultType === 'ok') {
          const itemsWithBase64Encoding = itemAsSimpleObjectsArray.value.map(
            (item) => {
              const sortedItem = copyObjectWithKeysSorted(item);
              const stringifiedItem = JSON.stringify(sortedItem);
              return {
                sortedObject: sortedItem,
                stringified: stringifiedItem,
              };
            },
          );

          newObj[key] = itemsWithBase64Encoding
            .sort((a, b) => {
              return a.stringified.localeCompare(b.stringified);
            })
            .map((item) => item.sortedObject);
        } else {
          newObj[key] = item;
        }
      }
    }
  }
  // TypeScript can't infer that newObj matches TSimpleObject here
  return newObj as TSimpleObject;
};
